<template>
    <vx-modal :visible="props.visible"
        @update:visible="emit('update:visible', $event)"
        @close="reset()"
        size="md"
        :padding="false"
        :stacked="configuring"
    >
        <template v-slot:header>
            <div class="pt-6 px-6 flex items-center gap-2">
                <span>
                    AI Message Assistant
                </span>
                <experimental-pill class="-mt-1"></experimental-pill>
            </div>
        </template>
        <template v-slot:subheader>
            <div class="px-6">Our AI-powered technology generates customized, compelling messages that engage customers and drive revenue growth.</div>
        </template>

        <div class="py-4 px-5 flex flex-col gap-8 lg:py-7 lg:px-10">
            <ai-configuration-status v-if="!generating && !allSuggestions.length" :requireSettings="false"  @saved="getAiConfig()" v-model:configuring="configuring" :unavailable="!!(error && hasAiConfig)"></ai-configuration-status>

            <div v-if="generating" data-test="generating-messages" class="flex flex-col items-center justify-center py-6 space-y-8">
                <div class="overflow-hidden">
                    <vx-icon-loading class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
                </div>
                <p class="text-center text-base text-primary lg:px-24">
                    Your messages are being generated by our advanced AI tool...
                </p>
            </div>

            <div v-else-if="!allSuggestions.length" :class="{
                'opacity-40': !hasAiConfig,
            }">
                <h4 class="text-base mb-3 font-bold text-slate-700">
                    Message Description
                </h4>
                <p class="text-sm mb-3 font-normal text-slate-500">
                    Please describe briefly what you'd like your message to be about.
                </p>
                <vx-input
                    :disabled="!hasAiConfig"
                    v-model="details"
                    :rows="4"
                    autoresize
                    placeholder="I'd like a message about..."
                ></vx-input>
            </div>

            <div v-else-if="allSuggestions.length">
                <h4 class="text-base mb-3 font-bold text-slate-700">
                    Generated Messages
                </h4>

                <message-generation-list v-model="selected" :messages="allSuggestions"></message-generation-list>
            </div>
        </div>
        <template v-if="!generating" v-slot:footer>
            <div class="flex gap-4">
                <template v-if="!hasAiConfig">
                    <vx-button @click="close()" type="button" color="muted" size="lg" class="grow">
                        Cancel <font-awesome-icon widthAuto :icon="faCircleXmark"></font-awesome-icon>
                    </vx-button>
                </template>
                <template v-else-if="!allSuggestions.length">
                    <vx-button @click="close()" type="button" color="muted" size="lg" class="grow-0">
                        Cancel <font-awesome-icon widthAuto :icon="faCircleXmark"></font-awesome-icon>
                    </vx-button>
                    <vx-button
                        @click.prevent="generate()"
                        class="grow"
                        type="button"
                        size="lg"
                        :loading="generating"
                        data-test="generate-message"
                    >
                        Generate AI Messages
                        <font-awesome-icon widthAuto
                            :icon="faWandMagicSparkles"
                        ></font-awesome-icon>
                    </vx-button>
                </template>
                <template v-else-if="allSuggestions.length">
                    <vx-button
                        class="grow-0"
                        color="muted-dark"
                        size="lg"
                        type="button"
                        @click="reset()"
                    >
                        Generate New
                        <font-awesome-icon widthAuto :icon="faWandMagicSparkles"></font-awesome-icon>
                    </vx-button>
                    <vx-button class="grow" type="button" size="lg" @click="confirm()" data-test="confirm-message">
                        Confirm
                        <font-awesome-icon widthAuto :icon="faArrowRight"></font-awesome-icon>
                    </vx-button>
                </template>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import {
    faArrowRight,
    faWandMagicSparkles,
    faCircleXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxModal, VxInput, VxButton, VxIconLoading } from '@voxie/frontend-components';
import { ref, watch } from 'vue';
import { aiClient , openAISettingsClient } from '~/services';
import MessageGenerationList from './MessageGenerationList.vue';
import ExperimentalPill from "~/components/general/ExperimentalPill.vue";
import AiConfigurationStatus from "~/components/settings/ai-configuration/AiConfigurationStatus.vue"

const props = defineProps({
    campaignType: {
        type: String,
        default: "marketing",
        validator: (campaignType) =>
            ["marketing", "transactional"].includes(campaignType),
    },
    visible: Boolean,
});

const emit = defineEmits(["selected", "update:visible"]);

const configuring = ref(false)

const details = ref("");

const allSuggestions = ref([]);
const selected = ref("");

const error = ref(false);
const generating = ref(false);

const hasAiConfig = ref(false);
const getAiConfig = async () => {
    if (hasAiConfig.value) {
        return;
    }
    try {
        await openAISettingsClient.getConfig(Spark.state.currentTeam.id);
        hasAiConfig.value = true;
    } catch (e) {
        console.error(e)
        hasAiConfig.value = false;
    }
}

watch([() => props.visible], () => {
    getAiConfig();
}, {
    immediate: true
})

const generate = async () => {
    if (!details.value) {
        return;
    }

    error.value = false;

    if (generating.value) {
        return;
    }

    generating.value = true;

    const additionalPrompt = props.campaignType === 'marketing' ? `Create a ${props.campaignType} campaign message with these details: ` : '';

    try {
        const response = await aiClient.quickBlastSuggestions({ teamId: Spark.state.currentTeam.id }, {
            details: `${additionalPrompt} ${details.value}`
        })
        allSuggestions.value = response.data.data.map(choice => choice?.message).filter(message => message?.length);

        if (!allSuggestions.value.length) {
            error.value = true;
        }
    } catch (e) {
        console.error(e)
        error.value = true;
    } finally {
        generating.value = false;
    }
};

const confirm = () => {
    if (!selected.value){
        selected.value = allSuggestions.value[0];
    }

    emit("selected", selected.value);
    emit('update:visible', false);
    reset();
};

const reset = () => {
    allSuggestions.value = [];
    selected.value = "";
    error.value = false;
}

const close = () => {
    emit('update:visible', false);
}
</script>
